import React, { useState } from "react";
import { FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { eaParams, useEa } from "./EaContext";
import { useSelectedCodes } from "./EaControls";
import { inputCls } from "./EaUi";

// Download Excel delle curve di consumo e di costo (kWh × PUN) su periodo libero.
export function EaCurveExport({ year }) {
  const { filters } = useEa();
  const codes = useSelectedCodes();
  const y = year || new Date().getFullYear();
  const [f, setF] = useState({ from: `${y}-01-01`, to: `${y}-12-31`, resolution: "hour", per_point: false, include_cost: true, include_pun: false });
  const [busy, setBusy] = useState(false);
  const download = async () => {
    if (!f.from || !f.to || f.to < f.from) return toast.error("Periodo non valido");
    setBusy(true);
    try {
      const r = await api.get("/ea/power/curves.xlsx", { params: eaParams(filters, { codes, date_from: f.from, date_to: f.to, resolution: f.resolution, per_point: f.per_point, include_cost: f.include_cost, include_pun: f.include_pun }), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      Object.assign(document.createElement("a"), { href: url, download: `Curve_consumo_costo_${f.from}_${f.to}.xlsx` }).click();
      URL.revokeObjectURL(url);
    } catch (e) {
      const t = e?.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : null;
      toast.error(t || "Download non riuscito");
    }
    setBusy(false);
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm inline-flex items-center gap-2" data-testid="ea-curve-export-open"><FileSpreadsheet size={15} /> Curve Excel</button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3" data-testid="ea-curve-export-panel">
        <div className="font-semibold text-slate-900 text-sm">Scarica curve di consumo e di costo</div>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs text-slate-600">Dal<input type="date" lang="it-IT" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} className={`${inputCls} mt-1 w-full`} data-testid="ea-curve-export-from" /></label>
          <label className="text-xs text-slate-600">Al<input type="date" lang="it-IT" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} className={`${inputCls} mt-1 w-full`} data-testid="ea-curve-export-to" /></label>
        </div>
        <div className="flex gap-2 text-xs">
          {[["hour", "Oraria"], ["quarter", "15 minuti"]].map(([k, l]) => (
            <button key={k} onClick={() => setF({ ...f, resolution: k })} data-testid={`ea-curve-export-res-${k}`}
              className={`px-3 py-1.5 rounded-md border font-semibold ${f.resolution === k ? "bg-[#071B4D] text-white border-[#071B4D]" : "border-slate-200 text-slate-600"}`}>{l}</button>
          ))}
        </div>
        <label className="text-xs text-slate-600 flex items-center gap-2"><input type="checkbox" checked={f.per_point} onChange={(e) => setF({ ...f, per_point: e.target.checked })} data-testid="ea-curve-export-per-point" /> Una riga per POD (altrimenti totale dei POD selezionati)</label>
        <label className="text-xs text-slate-600 flex items-center gap-2"><input type="checkbox" checked={f.include_cost} onChange={(e) => setF({ ...f, include_cost: e.target.checked })} data-testid="ea-curve-export-cost" /> Includi curva di costo PUN € (kWh × PUN)</label>
        <label className="text-xs text-slate-600 flex items-center gap-2"><input type="checkbox" checked={f.include_pun} onChange={(e) => setF({ ...f, include_pun: e.target.checked })} data-testid="ea-curve-export-pun" /> Includi curva dei prezzi PUN €/MWh</label>
        <div className="text-[11px] text-slate-500">Sempre incluso il foglio consumi kWh. Il costo PUN è il costo teorico della sola materia prima, non il costo fatturato. Massimo 366 giorni.</div>
        <button onClick={download} disabled={busy} className="w-full h-9 rounded-lg bg-slate-900 text-white text-sm font-semibold disabled:opacity-60" data-testid="ea-curve-export-download">{busy ? "Preparazione…" : "Scarica Excel"}</button>
      </PopoverContent>
    </Popover>
  );
}
